import { useEffect, useState } from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import userRoles from '../../utils/userRoles';
import { useAuthStore } from '../../context/useStore';
import { isRedirecting, startSilentSignIn } from '../../utils/auth';

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('currentUser'));
  } catch {
    return null;
  }
};

/*
  Checks the server session on every navigation (/api/check-auth, server/auth/saml/index.js):
  - session older than 8 h: re-confirm with Microsoft now, before the page is used (silent, comes back here)
  - session ended (24 h maximum, signed out at Microsoft, new deploy) while this browser was signed in: same
  - never signed in here, or signed out on purpose: the login page
*/
const ProtectedRoute = ({ roles = [...Object.values(userRoles)], userIds = null, flag = null }) => {
  const location = useLocation();
  const { user, setUser } = useAuthStore();
  const activeUser = user || getStoredUser();
  const isRoleAuthorized = roles.includes(activeUser?.role);
  const isUserAuthorized = !Array.isArray(userIds) || userIds.length === 0 || userIds.includes(activeUser?.id);
  // flag: name of a server-computed boolean on the user object (e.g. 'bankingAccess')
  const isFlagAuthorized = !flag || Boolean(activeUser?.[flag]);
  const isAuthorized = isRoleAuthorized && isUserAuthorized && isFlagAuthorized;
  const [isAuthenticated, setAuthenticated] = useState(false);
  const [isLoading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const returnPath = `${location.pathname}${location.search}`;

    const checkAuth = async () => {
      let data = null;
      try {
        const response = await fetch('/api/check-auth', { cache: 'no-store' });
        data = response.ok ? await response.json() : null;
      } catch {
        data = null;
      }
      if (cancelled) return;

      if (data?.message === 'Authenticated.') {
        setUser(data.user);
        localStorage.setItem('currentUser', JSON.stringify(data.user));
        if (isRedirecting() || (data.session?.reauthenticate && startSilentSignIn(returnPath))) return;
        setAuthenticated(true);
      } else {
        const wasSignedIn = Boolean(getStoredUser());
        setUser(null);
        localStorage.removeItem('currentUser');
        localStorage.setItem('prevLocation', returnPath);
        if (isRedirecting() || (wasSignedIn && startSilentSignIn(returnPath))) return;
        setAuthenticated(false);
      }
      setLoading(false);
    };

    checkAuth();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, setUser]);

  if (isLoading) return null;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAuthorized) {
    return <Navigate to="/home" replace />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
